/** Offboarding tab of the tour-operator page (tenant retirement CONTRACT §9).
 *
 * The offboarding checklist a tenant gets when it is closed: what has to be undone outside the
 * platform (vendor webhooks, Twilio, the e-mail domain, keys and lists that name its code). Built
 * from the Preflight tab's parts — the status control, the action bar, the expandable detail with
 * the item's instructions, the run log — over its own list: `?checklist=offboarding` under the
 * query key `['preflight', 'items', id, 'offboarding']`. The Preflight tab and the setup banner
 * keep `['preflight', 'items', id]`, the onboarding checklist, so their counts never include an
 * offboarding item; an item mutation invalidates the shared prefix, so both lists re-read.
 *
 * Per item: **Blocks release** marks the items that still stand between the tenant and the
 * release of its code (`blocks_release` from the backend, judged by `offboardingRelease.ts`: open
 * work, and a completed live-check item); Run is offered where the server says the item
 * runs (`run_action`, the manually executable ones call `execute/`); Mark done records a manual
 * completion; **Skip…** sets an open item aside with a required reason. A skipped item shows its
 * reason; a superseded one (the tenant was reopened) what it was before, and takes no change.
 *
 * Drawn only while the tenant is not active and for `console_preflight_view`; every change needs
 * `console_preflight_manage`. The backend is always the authoritative check.
 */
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useState } from 'react';

import {
  items,
  type PreflightActionRun,
  type PreflightItem,
  type PreflightStatus,
} from '@/api/preflight';
import type { TourOperatorDetail } from '@/api/tourOperators';
import { AssignedGroupLink } from '@/features/preflight/AssignedGroupLink';
import { PreflightItemActionBar } from '@/features/preflight/PreflightItemActionBar';
import { PreflightStatusControl } from '@/features/preflight/PreflightStatusControl';
import {
  isOpenStatus,
  OFFBOARDING_STATUSES,
  STATUS_LABELS,
  statusChipColor,
} from '@/features/preflight/status';
import { usePermissions } from '@/hooks/usePermissions';

import { When } from '../sandbox/When';

import { releaseBlockingReason } from './offboardingRelease';
import { PreflightItemDetailContent } from './PreflightItemDetailContent';
import { PreflightItemRunLogDrawer, PreflightPreviewDialog } from './PreflightRunLog';
import { SkipOffboardingItemDialog } from './SkipOffboardingItemDialog';
import { usePreflightItemActions } from './usePreflightItemActions';

/** Its own key under the per-TO prefix — never the onboarding list's `['preflight', 'items', id]`. */
export const offboardingItemsKey = (topId: number) =>
  ['preflight', 'items', topId, 'offboarding'] as const;

/** Chevron + status + item + owner + actions. */
const COLS = 5;

/** Applicable items first, then inapplicable — each keeping its arrival order. */
function applicableFirst(list: PreflightItem[]): PreflightItem[] {
  return [...list.filter((i) => i.applicable), ...list.filter((i) => !i.applicable)];
}

/** What a skip recorded (`context.skip_reason`, `skipped_ts`), on a skipped item. */
function skipOf(item: PreflightItem): { reason: string; ts: string | null } | null {
  if (item.status !== 'skipped') return null;
  const { skip_reason: reason, skipped_ts: ts } = item.context ?? {};
  return {
    reason: typeof reason === 'string' ? reason : '',
    ts: typeof ts === 'string' ? ts : null,
  };
}

/** The status a superseded item had when the tenant was reopened (`context.previous_status`). */
function previousStatusOf(item: PreflightItem): PreflightStatus | null {
  if (item.status !== 'superseded') return null;
  const prev = item.context?.previous_status;
  return typeof prev === 'string' && prev in STATUS_LABELS ? (prev as PreflightStatus) : null;
}

export function OffboardingTab({ to }: { to: Pick<TourOperatorDetail, 'id' | 'code'> }) {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_preflight_manage');

  const [overrides, setOverrides] = useState<Record<number, boolean>>({});
  const [logItem, setLogItem] = useState<PreflightItem | null>(null);
  const [skipItem, setSkipItem] = useState<PreflightItem | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: offboardingItemsKey(to.id),
    queryFn: () => items.list(to.id, 'offboarding'),
    // Poll while any item is mid-run so async executions surface as they finish.
    refetchInterval: (q) =>
      (q.state.data ?? []).some((i) => i.status === 'started') ? 4000 : false,
  });

  const actions = usePreflightItemActions(to.id);

  // Open work shows its instructions; what is settled stays a one-liner until opened.
  const isExpanded = (item: PreflightItem) => overrides[item.id] ?? isOpenStatus(item.status);
  const toggleRow = (item: PreflightItem) =>
    setOverrides((o) => ({ ...o, [item.id]: !isExpanded(item) }));

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !data) {
    return <Alert severity="error">Couldn’t load the offboarding checklist.</Alert>;
  }
  if (data.length === 0) {
    return (
      <Alert severity="info">
        No offboarding items for this tour operator. They are added when the tenant is closed.
      </Alert>
    );
  }

  // Status counts over the applicable items, plus an N/A count of the inapplicable ones.
  const counts = Object.fromEntries(OFFBOARDING_STATUSES.map((s) => [s, 0])) as Record<
    PreflightStatus,
    number
  >;
  for (const i of data) {
    if (i.applicable) counts[i.status] += 1;
  }
  // Superseded is the trace of a reopen: counted only when there is one.
  const shownStatuses = OFFBOARDING_STATUSES.filter(
    (s) => s !== 'superseded' || counts.superseded > 0,
  );
  const naCount = data.filter((i) => !i.applicable).length;
  const notImplementedCount = data.filter((i) => i.applicable && !i.implemented).length;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 900 }}>
        What has to be undone outside the platform now that this tenant is closed: the booking
        systems’ webhooks, its numbers, its sending domain, and the keys and lists that name its
        code. Items marked <strong>Blocks release</strong> must be done, or skipped with a reason,
        before the code can be released.
      </Typography>

      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
        {shownStatuses.map((s) => (
          <Chip
            key={s}
            size="small"
            color={statusChipColor(s)}
            variant={counts[s] ? 'filled' : 'outlined'}
            label={`${STATUS_LABELS[s]}: ${counts[s]}`}
          />
        ))}
        <Chip size="small" variant={naCount ? 'filled' : 'outlined'} label={`N/A: ${naCount}`} />
        {notImplementedCount > 0 && (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={`Not Implemented: ${notImplementedCount}`}
          />
        )}
      </Stack>

      {!canManage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have read-only access. The `console_preflight_manage` permission is required to run,
          complete or skip items.
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="Offboarding items" sx={{ '& td, & th': { py: 0.5 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 44 }} />
              <TableCell sx={{ width: 132 }}>Status</TableCell>
              <TableCell>Item</TableCell>
              <TableCell sx={{ width: 140 }}>Owner</TableCell>
              <TableCell align="right" sx={{ width: 168 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {applicableFirst(data).map((item) => (
              <OffboardingItemRow
                key={item.id}
                item={item}
                topId={to.id}
                toCode={to.code}
                canManage={canManage}
                pending={actions.isBusy(item.id)}
                expanded={isExpanded(item)}
                onToggleExpand={() => toggleRow(item)}
                onChanged={actions.invalidate}
                onStatusChange={(status) => actions.onStatusChange(item.id, status)}
                onStepChange={(step, status) => actions.onStepChange(item.id, step, status)}
                onPreview={() => actions.onPreview(item.id)}
                onRun={() => actions.onRun(item.id)}
                onExecute={() => actions.onExecute(item.id)}
                onComplete={() => actions.onComplete(item.id)}
                onConfigure={(values) => actions.onConfigure(item.id, values)}
                onViewLog={() => setLogItem(item)}
                onSkip={() => setSkipItem(item)}
                lastSave={actions.lastConfigureRun(item.id)}
                onDismissLastSave={() => actions.dismissConfigureRun(item.id)}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <PreflightItemRunLogDrawer
        topId={to.id}
        itemId={logItem?.id ?? null}
        title={logItem?.title}
        open={logItem != null}
        onClose={() => setLogItem(null)}
      />
      <PreflightPreviewDialog run={actions.previewRun} onClose={actions.clearPreview} />
      <SkipOffboardingItemDialog topId={to.id} item={skipItem} onClose={() => setSkipItem(null)} />
    </Box>
  );
}

// --------------------------------------------------------------------------

interface RowProps {
  item: PreflightItem;
  topId: number;
  toCode: string;
  canManage: boolean;
  pending: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  onChanged: () => void;
  onStatusChange: (status: PreflightStatus) => void;
  onStepChange: (step: number, status?: PreflightStatus) => void;
  onPreview: () => void;
  onRun: () => void;
  onExecute: () => void;
  onComplete: () => void;
  onConfigure: (values: Record<string, unknown>) => Promise<PreflightActionRun>;
  onViewLog: () => void;
  onSkip: () => void;
  lastSave: PreflightActionRun | null;
  onDismissLastSave: () => void;
}

function OffboardingItemRow({
  item,
  topId,
  toCode,
  canManage,
  pending,
  expanded,
  onToggleExpand,
  onChanged,
  onStatusChange,
  onStepChange,
  onPreview,
  onRun,
  onExecute,
  onComplete,
  onConfigure,
  onViewLog,
  onSkip,
  lastSave,
  onDismissLastSave,
}: RowProps) {
  // Inapplicable: a single muted row with its reason, no controls, no detail.
  if (!item.applicable) {
    return (
      <TableRow hover sx={{ opacity: 0.55 }}>
        <TableCell />
        <TableCell>
          <Chip size="small" variant="outlined" label="N/A" />
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {item.title}
          </Typography>
          {item.applicable_reason && (
            <Typography variant="caption" color="text.disabled">
              {item.applicable_reason}
            </Typography>
          )}
        </TableCell>
        <TableCell colSpan={COLS - 3} />
      </TableRow>
    );
  }

  // Marked while it is still in the way: open work, or a completed live-check item (its
  // condition is checked again at release); skipped or superseded, it no longer blocks.
  const blocking = releaseBlockingReason(item);
  const skip = skipOf(item);
  const previous = previousStatusOf(item);
  // A superseded item is the trace of a reopen (a re-close resets it to pending): nothing on
  // it is to be run, completed or edited, so it is read-only whatever the permission.
  const writable = canManage && item.status !== 'superseded';

  return (
    <Fragment>
      <TableRow hover data-action={item.action} sx={{ '& > td': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            size="small"
            onClick={onToggleExpand}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>
          {canManage ? (
            <PreflightStatusControl
              status={item.status}
              disabled={pending}
              onComplete={onComplete}
              onStatusChange={onStatusChange}
            />
          ) : (
            <Chip
              size="small"
              label={STATUS_LABELS[item.status]}
              color={statusChipColor(item.status)}
            />
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {item.title}
          </Typography>
          {(blocking || !item.implemented) && (
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
              {blocking && (
                <Tooltip describeChild title={blocking}>
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label="Blocks release"
                    // Focusable, so the tooltip's reason reaches the keyboard too.
                    tabIndex={0}
                    sx={{ height: 18 }}
                  />
                </Tooltip>
              )}
              {!item.implemented && (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label="Not Implemented"
                  sx={{ height: 18 }}
                />
              )}
            </Stack>
          )}
          {skip && (
            <Typography variant="caption" color="text.secondary" component="div">
              Skipped
              {skip.ts && (
                <>
                  {' '}
                  <When iso={skip.ts} />
                </>
              )}
              {skip.reason ? `: ${skip.reason}` : ''}
            </Typography>
          )}
          {item.status === 'superseded' && (
            <Typography variant="caption" color="text.secondary" component="div">
              Superseded when the tenant was reopened
              {previous ? ` — it was ${STATUS_LABELS[previous].toLowerCase()}` : ''}. Closing it
              again puts it back to pending.
            </Typography>
          )}
          {!expanded && item.status === 'error' && item.error_message && (
            <Typography variant="caption" color="error" sx={{ display: 'block' }}>
              {item.error_message}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <AssignedGroupLink item={item} />
        </TableCell>
        <TableCell align="right">
          <PreflightItemActionBar
            item={item}
            canManage={writable}
            busy={pending}
            onPreview={onPreview}
            onRun={onRun}
            onExecute={onExecute}
            onComplete={onComplete}
            onViewLog={onViewLog}
            onSkip={onSkip}
          />
        </TableCell>
      </TableRow>

      {/* Detail — the item's instructions (its rendered steps), any custom UI and the full error. */}
      <TableRow>
        <TableCell colSpan={COLS} sx={{ py: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1.5, pl: 6, pr: 2 }}>
              <PreflightItemDetailContent
                item={item}
                topId={topId}
                toCode={toCode}
                canManage={writable}
                pending={pending}
                onConfigure={onConfigure}
                onStepChange={onStepChange}
                onChanged={onChanged}
                lastSave={lastSave}
                onDismissLastSave={onDismissLastSave}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}
